import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faBrain, faCloud, faServer } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';

function Projects() {
  const [visibleProjects, setVisibleProjects] = useState([]);
  const projectRefs = useRef([]);

  useEffect(() => {
    const observers = projectRefs.current.map((ref, index) => {
      if (!ref) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleProjects((prev) => {
                if (!prev.includes(index)) {
                  return [...prev, index];
                }
                return prev;
              });
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(ref);
      return observer;
    });

    return () => {
      observers.forEach((observer) => {
        if (observer) observer.disconnect();
      });
    };
  }, []);

  const projects = [
    {
      title: 'AI-Driven Automation with n8n & Google Cloud',
      year: '2025',
      icon: faCloud,
      description: 'A personal automation system built using n8n, AI agents, and Google Cloud. It automates daily tasks such as email screening, tracking expenses, and offering a fast Telegram chatbot for quick interactions. The system acts as a modular workflow hub designed to reduce manual work and improve daily productivity.',
      tech: ['n8n', 'Google Cloud', 'AI Agents', 'Telegram Bot', 'Automation'],
      github: null
    },
    {
      title: 'Self-Hosted Home Server & Service Hub',
      year: '2024',
      icon: faServer,
      description: 'A centralized server hosting core network and productivity services, including Pi-hole for DNS filtering, n8n for workflow automation, and Nextcloud for private file storage. This setup creates a secure, self-controlled foundation for my personal projects and daily digital life.',
      tech: ['Pi-hole', 'n8n', 'Nextcloud', 'Self-Hosted', 'Docker', 'Linux'],
      github: null
    },
    {
      title: 'Real-Time Anomaly Detection on UR Robot using Siemens Edge',
      year: '2024',
      icon: faRobot,
      description: 'An anomaly-detection application deployed on Siemens Industrial Edge to monitor real-time data from a UR robot. It identifies unusual activity using machine learning models and sends alerts through a secure API. This helps engineers respond quickly to abnormal robot behavior in industrial environments.',
      tech: ['Siemens Edge', 'Machine Learning', 'UR Robot', 'Python', 'Real-Time'],
      github: 'https://github.com/manofculturett/DemoIEAPP'
    },
    {
      title: 'Machine Learning for Predicting Probiotic Benefits',
      year: '2023',
      icon: faBrain,
      description: 'A machine-learning project focused on predicting probiotic effects using clinical and biological data. The system uses regression and gradient-boosting models to estimate potential benefits early and improve evaluation workflows.',
      tech: ['Machine Learning', 'Python', 'scikit-learn', 'XGBoost', 'Regression'],
      github: null
    }
  ];

  return (
    <div className="App-section">
      <h2>Projects</h2>
      <div className="projects-container">
        {projects.map((project, index) => (
          <div
            key={index}
            ref={(el) => (projectRefs.current[index] = el)}
            className={`project-card ${visibleProjects.includes(index) ? 'visible' : ''}`}
          >
            <div className="project-header">
              <div className="project-title-section">
                <FontAwesomeIcon icon={project.icon} className="project-icon" />
                <h3>{project.title}</h3>
              </div>
              <span className="project-year">{project.year}</span>
            </div>
            <p className="project-description">{project.description}</p>
            <div className="tech-badges">
              {project.tech.map((tech, techIndex) => (
                <span key={techIndex} className="tech-badge">{tech}</span>
              ))}
            </div>
            {project.github && (
              <div className="project-links">
                <a
                  href={project.github}
                  className="project-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon={faGithub} /> GitHub
                </a>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Projects;